/**
 * Set up — shadow, then propose, then promote.
 *
 * The two endpoints behind this screen shipped a long time ago and **nothing in any UI ever
 * called them**. That is the whole reason the adoption story ("start where nothing can break")
 * was a paragraph on the marketing site with no product behind it.
 *
 * `GET /agent/would-have-blocked` reports the calls that went through under a non-enforcing
 * mandate that an enforcing gate would have stopped. `GET /agent/{id}/mandate-proposal` turns
 * observed behaviour into a mandate that would have allowed everything the agent actually did
 * and nothing more — so turning the gate on breaks nothing.
 *
 * Both read the shadow window, and both are empty until something has actually run through the
 * gate un-enforced. An empty screen here means "no shadow traffic yet", not "broken", and it
 * says so.
 */

import { useState } from "react";
import { State, fmtMoney, useAsync } from "@authoxi/ui-shared";
import type { Api } from "../api";

export function Setup({ api }: { api: Api }) {
  const agents = useAsync(() => api.agents());
  const mandates = useAsync(() => api.mandates());
  const shadow = useAsync(() => api.wouldHaveBlocked());
  const tenant = useAsync(() => api.tenant());

  const shadowed = (mandates.data ?? []).filter(
    (m) => m.status === "active" && m.enforcement !== "deny",
  );
  const [target, setTarget] = useState<string | null>(null);
  const chosen = target ?? shadowed[0]?.subject_id ?? null;

  const steps = [
    { done: !!tenant.data, n: "1", h: "Get a key", d: tenant.data ? `Done — ${tenant.data.tenant_id}.` : "" },
    {
      done: (agents.data?.length ?? 0) > 0,
      n: "2",
      h: "Register an agent",
      d: agents.data ? `${agents.data.length} registered.` : "",
    },
  ];

  return (
    <>
      <div className="view-head">
        <span className="label">Set up</span>
        <h1>
          {shadowed.length ? "You are one step from enforcing." : "Nothing is in shadow."}
        </h1>
        <p>
          Nothing here blocks anything until you say so. Shadow mode watches first and tells you
          what it would have refused, so you find out what your fleet actually does before you
          change what it can do.
        </p>
      </div>

      {steps.map((s) => (
        <div className={`step ${s.done ? "done" : ""}`} key={s.n}>
          <span className="step-n">{s.done ? "✓" : s.n}</span>
          <div>
            <h3>{s.h}</h3>
            <p className="sm muted" style={{ margin: 0 }}>
              {s.d || "Not yet."}
            </p>
          </div>
        </div>
      ))}

      <div className="step now">
        <span className="step-n">3</span>
        <div style={{ width: "100%" }}>
          <h3>Promote a proposed mandate</h3>

          {shadow.loading && <State kind="loading" title="Reading the shadow window…" />}

          {shadowed.length === 0 && (
            <State
              kind="empty"
              title="No agent is running in shadow."
              detail="Issue a mandate with enforcement `dryrun` and let the agent run. This screen fills up from what it actually does — it cannot propose anything from nothing."
            />
          )}

          {shadowed.length > 1 && (
            <div className="row" style={{ margin: "var(--s3) 0" }}>
              {shadowed.map((m) => (
                <button
                  key={m.subject_id}
                  className={`btn ${chosen === m.subject_id ? "btn-go" : ""}`}
                  onClick={() => setTarget(m.subject_id)}
                >
                  {m.subject_id}
                </button>
              ))}
            </div>
          )}

          {chosen && <Proposal api={api} agentId={chosen} onPromoted={() => void mandates.reload()} />}
        </div>
      </div>

      {shadow.data && shadow.data.window_size > 0 && (
        <div className="card pad" style={{ marginTop: "var(--s6)" }} data-testid="would-have-blocked">
          <p className="label">What it would have blocked</p>
          <p className="sm" style={{ margin: "var(--s3) 0 0", color: "var(--ink-2)" }}>
            Over {shadow.data.window_size} observed decisions, an enforcing gate would have held{" "}
            <strong>{shadow.data.caught}</strong> — {shadow.data.would_have_denied} refused and{" "}
            {shadow.data.would_have_escalated} sent to a human.
            {Object.entries(shadow.data.money_at_risk).map(([ccy, amount]) => (
              <span key={ccy}> {fmtMoney(amount, ccy)} would have been averted.</span>
            ))}
          </p>
          {Object.keys(shadow.data.by_reason).length > 0 && (
            <div className="stack" style={{ marginTop: "var(--s4)" }}>
              {Object.entries(shadow.data.by_reason).map(([reason, n]) => (
                <div className="between" key={reason}>
                  <span className="mono sm">{reason}</span>
                  <span className="sm muted">{n}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
}

function Proposal({
  api,
  agentId,
  onPromoted,
}: {
  api: Api;
  agentId: string;
  onPromoted: () => void;
}) {
  const proposal = useAsync(() => api.mandateProposal(agentId), [agentId]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  if (proposal.loading) return <State kind="loading" title="Deriving a proposal…" />;
  if (proposal.error) return <State kind="error" title="Could not derive one" detail={proposal.error} />;
  const p = proposal.data;
  if (!p) return null;

  if (p.observations_seen === 0) {
    return (
      <State
        kind="empty"
        title="This agent has not done anything yet."
        detail="A proposal is derived from observed behaviour. Let it run, then come back."
      />
    );
  }

  if (done) {
    return (
      <div className="soft pad" data-testid="promoted">
        <span className="v dim v-allow">enforcing</span>{" "}
        <span className="sm muted">
          The gate is on for {agentId}. Its next out-of-policy call is a real denial.
        </span>
      </div>
    );
  }

  return (
    <div data-testid="proposal">
      <p className="sm" style={{ margin: "0 0 var(--s3)", color: "var(--ink-2)", maxWidth: "var(--measure)" }}>
        From {p.observations_seen} observed actions, this mandate would have allowed everything{" "}
        <span className="mono">{agentId}</span> actually did and nothing more — so turning the
        gate on breaks nothing.
      </p>

      <div className="soft pad" style={{ marginBottom: "var(--s3)" }}>
        <span className="label">Proposed</span>
        <div className="stack" style={{ marginTop: "var(--s3)" }}>
          <div className="row">
            <span className="v dim v-allow">allow</span>
            <span className="mono sm">{p.allowed_actions.join(" · ") || "nothing observed"}</span>
          </div>
          {p.escalate_over && (
            <div className="row">
              <span className="v v-escalate">escalate</span>
              <span className="sm">over {fmtMoney(p.escalate_over, p.currency)}</span>
            </div>
          )}
          <div className="row">
            <span className="v dim v-deny">deny</span>
            <span className="sm muted">everything else it never did</span>
          </div>
        </div>
        <p className="xs muted" style={{ marginTop: "var(--s3)" }}>
          Budget {fmtMoney(p.budget, p.currency)} · covers {Math.round(p.coverage_ratio * 100)}% of
          what was observed.
        </p>
        {p.notes.map((n) => (
          <p className="xs muted" key={n}>
            {n}
          </p>
        ))}
      </div>

      <button
        className="btn btn-go"
        disabled={busy}
        data-testid="promote"
        onClick={async () => {
          setBusy(true);
          setErr(null);
          try {
            await api.issueMandate({
              subject_id: agentId,
              budget: p.budget,
              currency: p.currency,
              allowed_actions: p.allowed_actions,
              escalate_over: p.escalate_over,
              enforcement: "deny",
            });
            setDone(true);
            onPromoted();
          } catch (e) {
            setErr(e instanceof Error ? e.message : String(e));
          } finally {
            setBusy(false);
          }
        }}
      >
        {busy ? "…" : "Turn on the gate"}
      </button>
      {err && <p className="sm" style={{ color: "var(--deny-text)" }}>{err}</p>}
      <p className="xs muted" style={{ marginTop: "var(--s2)" }}>
        Reversible: the shadow mandate stays, and you can revoke the enforcing one.
      </p>
    </div>
  );
}
